import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import {
  makeStyles,
  Paper,
  Typography,
  Grid,
  CircularProgress
} from "@material-ui/core";
import TodayIcon from "@material-ui/icons/Today";
import DateRangeIcon from "@material-ui/icons/DateRange";
import EventNoteIcon from "@material-ui/icons/EventNote";
import TimerIcon from "@material-ui/icons/Timer";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh"
  },
  pageTitle: {
    marginBottom: theme.spacing(3),
    fontWeight: 700
  },
  card: {
    padding: theme.spacing(2.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
  },
  cardIcon: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0
  },
  cardValue: {
    fontSize: "2rem",
    fontWeight: 700,
    lineHeight: 1
  },
  cardLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: 2
  },
  chartPaper: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
  },
  chartTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  roiPaper: {
    padding: theme.spacing(3),
    borderRadius: 12,
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)"
  },
  roiTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    marginBottom: theme.spacing(1)
  },
  roiValue: {
    fontSize: "2.5rem",
    fontWeight: 800,
    lineHeight: 1
  },
  roiSub: {
    opacity: 0.85,
    marginTop: theme.spacing(0.5)
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200
  }
}));

const COLORS = ["#667eea", "#f59e0b"];

const MetricCard = ({ icon, iconBg, value, label }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.cardIcon} style={{ background: iconBg }}>
        {icon}
      </div>
      <div>
        <div className={classes.cardValue}>{value ?? "—"}</div>
        <div className={classes.cardLabel}>{label}</div>
      </div>
    </Paper>
  );
};

const Stats = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: stats } = await api.get("/stats/company");
        setData(stats);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loader}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  const pieData = data
    ? [
        { name: i18n.t("stats.ia"), value: data.aiMessagesTotal || 0 },
        { name: i18n.t("stats.humano"), value: data.humanMessagesTotal || 0 }
      ]
    : [];

  // Format dates for X axis — show only day/month
  const dailyFormatted = (data?.daily || []).map(d => ({
    ...d,
    label: d.date.slice(5) // "MM-DD"
  }));

  return (
    <div className={classes.root}>
      <Typography variant="h5" className={classes.pageTitle}>
        {i18n.t("stats.titulo")}
      </Typography>

      {/* Metric cards */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<TodayIcon />}
            iconBg="#667eea"
            value={data?.ticketsToday}
            label={i18n.t("stats.ticketsHoy")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<DateRangeIcon />}
            iconBg="#10b981"
            value={data?.ticketsWeek}
            label={i18n.t("stats.ticketsSemana")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<EventNoteIcon />}
            iconBg="#f59e0b"
            value={data?.ticketsMonth}
            label={i18n.t("stats.ticketesMes")}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={<TimerIcon />}
            iconBg="#ef4444"
            value={
              data?.avgResponseMinutes != null
                ? `${data.avgResponseMinutes} min`
                : "—"
            }
            label={i18n.t("stats.tiempoRespuesta")}
          />
        </Grid>
      </Grid>

      {/* ROI Banner */}
      {data && (
        <Grid container spacing={2} style={{ marginBottom: 24 }}>
          <Grid item xs={12}>
            <Paper className={classes.roiPaper} elevation={0}>
              <Typography className={classes.roiTitle}>
                {i18n.t("stats.impactoIA")}
              </Typography>
              <Typography className={classes.roiValue}>
                {i18n.t("stats.horasAhorradas", { hours: data.hoursSaved })}
              </Typography>
              <Typography className={classes.roiSub}>
                {i18n.t("stats.equivalente", { days: data.daysSaved, ai: data.aiMessagesTotal, human: data.humanMessagesTotal })}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={2}>
        {/* Bar chart */}
        <Grid item xs={12} md={8}>
          <Paper className={classes.chartPaper} elevation={0}>
            <Typography variant="subtitle1" className={classes.chartTitle}>
              {i18n.t("stats.conversacionesPorDia")}
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={dailyFormatted}
                margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  interval={4}
                />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip
                  formatter={val => [val, i18n.t("stats.tickets")]}
                  labelFormatter={l => `${i18n.t("stats.fecha")}: ${l}`}
                />
                <Bar dataKey="count" fill="#667eea" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Pie chart */}
        <Grid item xs={12} md={4}>
          <Paper className={classes.chartPaper} elevation={0}>
            <Typography variant="subtitle1" className={classes.chartTitle}>
              {i18n.t("stats.mensajesIAvsHumano")}
            </Typography>
            {pieData[0].value === 0 && pieData[1].value === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 16 }}
              >
                {i18n.t("stats.sinDatos")}
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={4}
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip formatter={(val, name) => [val, name]} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default Stats;
